import { useId } from 'react'
import { CheckCircle2, Zap } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AppraisalField } from '@/components/deals/appraisal/AppraisalField'
import { formatNumber } from '@/components/deals/appraisal/appraisal-calculations'
import { marketInputGuidance } from '@/components/deals/appraisal/appraisal-market-guidance'
import { cn } from '@/lib/utils'

const DEFAULT_STATE = {
  exitMode: 'Hold',
  propertyType: 'Flat',
  bedroomCount: '',
  valuationMethod: 'Comparable',
  suggestedGdv: '',
  adoptedGdv: '',
  suggestedSqft: '',
  adoptedSqft: '',
  internalArea: '',
  operatingModel: 'AST',
  suggestedMonthlyRent: '',
  adoptedMonthlyRent: '',
  astOpexMode: 'percentage',
  astOpexPercent: '',
  astAnnualOperatingCost: '',
  suggestedAdr: '',
  adoptedAdr: '',
  occupancy: '75',
  opexPercent: '25',
}

const EXIT_MODES = ['Hold', 'Sell']
const PROPERTY_TYPES = [
  '1-Bed Flat',
  '2-Bed Flat',
  '3-Bed Flat',
  '2-Bed Terraced House',
  '3-Bed Terraced House',
  '2-Bed Semi-Detached House',
  '3-Bed Semi-Detached House',
  '4-Bed Detached House',
  'Flat',
  'Terraced House',
  'Semi-Detached House',
  'Detached House',
  'Other',
]
const OPERATING_MODELS = ['AST', 'SA']
const AST_OPEX_MODES = [
  { value: 'percentage', label: '% of Gross Rent' },
  { value: 'annual_cost', label: 'Annual Cost' },
]
const VALUATION_METHODS = ['Comparable', '£/sqft']

export function BrrrFlipScheme({ state, postcodeArea, showValidationErrors, onStateChange }) {
  const schemeState = { ...DEFAULT_STATE, ...state }
  const calculatedGdv = numberFromInput(schemeState.adoptedSqft) * numberFromInput(schemeState.internalArea)
  const hasSizeAdjustedGdv = (
    numberFromInput(schemeState.suggestedSqft) > 0 &&
    numberFromInput(schemeState.internalArea) > 0
  )
  const adoptedGdvValue = schemeState.valuationMethod === '£/sqft'
    ? String(formatNumber(calculatedGdv))
    : schemeState.adoptedGdv
  const netMonthlyIncome = (
    numberFromInput(schemeState.adoptedAdr) *
    30 *
    (numberFromInput(schemeState.occupancy) / 100) *
    (1 - numberFromInput(schemeState.opexPercent) / 100)
  )
  const monthlyRent = numberFromInput(schemeState.adoptedMonthlyRent)
  const grossAnnualAstRent = monthlyRent * 12
  const annualAstOpex = schemeState.astOpexMode === 'annual_cost'
    ? numberFromInput(schemeState.astAnnualOperatingCost)
    : grossAnnualAstRent * (numberFromInput(schemeState.astOpexPercent) / 100)
  const derivedAstOpexPercent = grossAnnualAstRent > 0
    ? (annualAstOpex / grossAnnualAstRent) * 100
    : 0
  const astNetOperatingIncome = Math.max(grossAnnualAstRent - annualAstOpex, 0)
  const incomeLabel = schemeState.operatingModel === 'AST' ? 'Monthly Rent' : 'Net Monthly'
  const incomeValue = schemeState.operatingModel === 'AST'
    ? schemeState.adoptedMonthlyRent
    : formatNumber(netMonthlyIncome)

  function updateField(field, value) {
    onStateChange?.({ ...schemeState, [field]: value }, { field })
  }

  function updateInput(field) {
    return (event) => updateField(field, event.target.value)
  }

  function marketGuidance(suggestedValue, adoptedValue) {
    return marketInputGuidance({
      suggestedValue,
      adoptedValue,
      postcodeArea,
      showValidationErrors,
    })
  }

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <SectionTitle>1. Deal Setup</SectionTitle>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          <SegmentedControl
            label="Exit Mode"
            options={EXIT_MODES}
            value={schemeState.exitMode}
            onChange={(value) => updateField('exitMode', value)}
          />
          <LabeledSelect
            label="Property Type"
            value={schemeState.propertyType}
            options={PROPERTY_TYPES}
            onValueChange={(value) => updateField('propertyType', value)}
          />
          <AppraisalField
            label="Bedroom Count"
            value={schemeState.bedroomCount}
            type="number"
            inputMode="numeric"
            min="1"
            step="1"
            onChange={updateInput('bedroomCount')}
            className="bg-background font-bold"
          />
          <LabeledSelect
            label="Valuation Method"
            value={schemeState.valuationMethod}
            options={VALUATION_METHODS}
            onValueChange={(value) => updateField('valuationMethod', value)}
          />
        </div>
      </section>

      <section className="space-y-4">
        <SectionTitle>2. Valuation</SectionTitle>
        {schemeState.valuationMethod === 'Comparable' ? (
          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <ReadOnlyValue
                label="Suggested End Value / GDV"
                value={suggestedCurrency(schemeState.suggestedGdv)}
                icon={Zap}
              />
              {hasSizeAdjustedGdv ? (
                <p className="text-xs font-medium text-muted-foreground">
                  Based on £{schemeState.suggestedSqft}/sqft × {schemeState.internalArea} sqft.
                </p>
              ) : null}
            </div>
            <AppraisalField
              label="Adopted End Value / GDV *"
              value={schemeState.adoptedGdv}
              prefix="£"
              inputMode="decimal"
              {...marketGuidance(schemeState.suggestedGdv, schemeState.adoptedGdv)}
              onChange={updateInput('adoptedGdv')}
              className="bg-background font-bold"
            />
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-3">
            <ReadOnlyValue label="Suggested £/sqft" value={suggestedCurrency(schemeState.suggestedSqft)} icon={Zap} />
            <AppraisalField
              label="Adopted £/sqft *"
              value={schemeState.adoptedSqft}
              prefix="£"
              inputMode="decimal"
              {...marketGuidance(schemeState.suggestedSqft, schemeState.adoptedSqft)}
              onChange={updateInput('adoptedSqft')}
              className="bg-background font-bold"
            />
            <AppraisalField
              label="Internal Area *"
              value={schemeState.internalArea}
              suffix="sqft"
              inputMode="decimal"
              onChange={updateInput('internalArea')}
              className="bg-background font-bold"
            />
            <ReadOnlyValue label="Calculated End Value / GDV" value={`£${formatNumber(calculatedGdv)}`} />
            <div className="md:col-span-2">
              <AppraisalField
                label="Adopted End Value / GDV *"
                value={schemeState.adoptedGdv}
                prefix="£"
                inputMode="decimal"
                onChange={updateInput('adoptedGdv')}
                className="bg-background font-bold"
              />
            </div>
          </div>
        )}
      </section>

      {schemeState.exitMode === 'Hold' ? (
        <section className="space-y-4">
          <SectionTitle>3. Income</SectionTitle>
          <div className="space-y-5">
            <div className="md:w-1/3">
              <SegmentedControl
                label="Operating Model"
                options={OPERATING_MODELS}
                value={schemeState.operatingModel}
                onChange={(value) => updateField('operatingModel', value)}
              />
            </div>

            {schemeState.operatingModel === 'AST' ? (
              <div className="space-y-5">
                <div className="grid gap-5 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <ReadOnlyValue
                      label="Suggested Monthly Rent"
                      value={suggestedCurrency(schemeState.suggestedMonthlyRent)}
                      icon={Zap}
                    />
                    {schemeState.suggestedMonthlyRent && schemeState.bedroomCount ? (
                      <p className="text-xs font-medium text-muted-foreground">
                        Based on {postcodeArea || 'local'} {schemeState.bedroomCount}-bed market baseline.
                      </p>
                    ) : null}
                  </div>
                  <AppraisalField
                    label="Adopted Monthly Rent *"
                    value={schemeState.adoptedMonthlyRent}
                    prefix="£"
                    inputMode="decimal"
                    {...marketGuidance(
                      schemeState.suggestedMonthlyRent,
                      schemeState.adoptedMonthlyRent,
                    )}
                    onChange={updateInput('adoptedMonthlyRent')}
                    className="bg-background font-bold"
                  />
                </div>

                <div className="grid gap-5 md:grid-cols-3">
                  <SegmentedControl
                    label="OPEX Input"
                    options={AST_OPEX_MODES}
                    value={schemeState.astOpexMode}
                    onChange={(value) => updateField('astOpexMode', value)}
                  />
                  {schemeState.astOpexMode === 'annual_cost' ? (
                    <>
                      <AppraisalField
                        label="Annual Operating Cost"
                        value={schemeState.astAnnualOperatingCost}
                        prefix="£"
                        inputMode="decimal"
                        onChange={updateInput('astAnnualOperatingCost')}
                        className="bg-background font-bold"
                      />
                      <ReadOnlyValue
                        label="Equivalent OPEX"
                        value={`${formatNumber(derivedAstOpexPercent)}%`}
                      />
                    </>
                  ) : (
                    <>
                      <AppraisalField
                        label="OPEX (% of Gross Rent)"
                        value={schemeState.astOpexPercent}
                        suffix="%"
                        inputMode="decimal"
                        onChange={updateInput('astOpexPercent')}
                        className="bg-background font-bold"
                      />
                      <ReadOnlyValue
                        label="Annual Operating Cost"
                        value={`£${formatNumber(annualAstOpex)}`}
                      />
                    </>
                  )}
                  <ReadOnlyValue
                    label="Gross Annual Rent"
                    value={`£${formatNumber(grossAnnualAstRent)}`}
                  />
                  <ReadOnlyValue
                    label="Net Operating Income"
                    value={`£${formatNumber(astNetOperatingIncome)}`}
                  />
                </div>
              </div>
            ) : (
              <div className="grid gap-5 md:grid-cols-3">
                <ReadOnlyValue label="Suggested ADR" value={suggestedCurrency(schemeState.suggestedAdr)} icon={Zap} />
                <AppraisalField
                  label="Adopted ADR *"
                  value={schemeState.adoptedAdr}
                  prefix="£"
                  inputMode="decimal"
                  {...marketGuidance(schemeState.suggestedAdr, schemeState.adoptedAdr)}
                  onChange={updateInput('adoptedAdr')}
                  className="bg-background font-bold"
                />
                <AppraisalField
                  label="Occupancy *"
                  value={schemeState.occupancy}
                  suffix="%"
                  inputMode="decimal"
                  onChange={updateInput('occupancy')}
                  className="bg-background font-bold"
                />
                <AppraisalField
                  label="OPEX *"
                  value={schemeState.opexPercent}
                  suffix="%"
                  inputMode="decimal"
                  onChange={updateInput('opexPercent')}
                  className="bg-background font-bold"
                />
                <ReadOnlyValue label="Net Monthly Income (Pre-Fin)" value={`£${formatNumber(netMonthlyIncome)}`} />
                <ReadOnlyValue label="Net Annual Income (Pre-Fin)" value={`£${formatNumber(netMonthlyIncome * 12)}`} />
              </div>
            )}
          </div>
        </section>
      ) : null}

      <section className="border-t border-border/50 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-primary/20 bg-primary/5 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/20">
              <CheckCircle2 aria-hidden="true" className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-foreground">Scheme Summary</h4>
              <p className="text-xs font-medium text-muted-foreground">
                Adopted values driving the appraisal
              </p>
            </div>
          </div>
          <dl className="flex flex-wrap gap-6 text-right">
            <SummaryItem label="End Value / GDV" value={`£${adoptedGdvValue}`} />
            {schemeState.exitMode === 'Hold' ? (
              <SummaryItem
                label={incomeLabel}
                value={`£${schemeState.operatingModel === 'AST' ? formatNumber(monthlyRent) : incomeValue}`}
              />
            ) : null}
          </dl>
        </div>
      </section>
    </div>
  )
}

function SectionTitle({ children }) {
  return (
    <h3 className="border-b border-border/50 pb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
      {children}
    </h3>
  )
}

function SegmentedControl({ label, options, value, onChange }) {
  const normalizedOptions = options.map((option) => (
    typeof option === 'string' ? { value: option, label: option } : option
  ))

  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div
        className="flex rounded-lg border border-border/50 bg-muted/30 p-1"
        role="group"
        aria-label={label}
      >
        {normalizedOptions.map((option) => (
          <Button
            key={option.value}
            type="button"
            variant="ghost"
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
            className={cn(
              'h-auto flex-1 rounded-md border border-transparent py-1.5 text-[11px] font-bold text-muted-foreground shadow-none hover:bg-background hover:text-foreground',
              value === option.value && 'border-border/50 bg-background text-foreground shadow-sm hover:bg-background',
            )}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  )
}

function LabeledSelect({ label, value, options, onValueChange }) {
  const selectId = useId()

  return (
    <div className="space-y-1.5">
      <Label
        htmlFor={selectId}
        className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
      >
        {label}
      </Label>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger id={selectId} className="w-full bg-muted/30 font-bold" aria-label={label}>
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function ReadOnlyValue({ label, value, icon: Icon }) {
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="flex min-h-9 items-center gap-2 rounded-lg border border-border/30 bg-muted/10 px-3 py-2.5 text-sm font-medium text-muted-foreground">
        {Icon ? <Icon aria-hidden="true" className="h-3.5 w-3.5 text-primary" /> : null}
        {value}
      </p>
    </div>
  )
}

function SummaryItem({ label, value }) {
  return (
    <div>
      <dt className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </dt>
      <dd className="text-base font-bold text-foreground">{value}</dd>
    </div>
  )
}

function suggestedCurrency(value) {
  return value ? `£${value}` : 'Unavailable'
}

function numberFromInput(value) {
  return Number(String(value ?? '').replace(/,/g, '')) || 0
}
